// The front end's animated backdrop behind the HTML pages (home page, lobby; docs/platform/
// ClientShell.md "Lobby style"): the theme's `FrontendResource` mesh (Scripts/Themes.txt,
// `FE_AnimatedBackground_<Theme>_01`: sky, landscape and worms acting in front of it) as
// wmd_frontend's `XOM_Background_01` shows it: the 1920x1080 design space through the IP2 capture
// camera (render/uixom.ts: frustum half-extent FRUSTUM_X at near 1) at (0, 0, 5000) looking down
// -Z, solid, its "Go" clip (160 s) looping. Here it covers the window as CSS `object-fit: cover`
// would, drawn straight into its own canvas 30 times a second (no capture, no 2-D copy). With
// reduced motion it holds the clip's first frame.
import * as THREE from 'three';
import { themeBackdrop } from '../ui/feart.ts';
import { findMesh, instantiate, loadTemplate, poseLayers, type MeshInstance } from './meshes.ts';
import { FRUSTUM_X } from './uixom.ts';

/** `XOM_Background_01`: its design size and `m_CameraPositionZ`, its default animation. */
const DESIGN_W = 1920, DESIGN_H = 1080, CAMERA_Z = 5000, CLIP = 'Go';
const FRAME_MS = 1000 / 30;

export class Backdrop {
  /** The fixed full-window layer (`.backdrop`, `.ready` once a frame is drawn). */
  readonly el = document.createElement('div');
  private canvas = document.createElement('canvas');
  private gl: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(20, DESIGN_W / DESIGN_H, 1, 10000);
  private theme: string | null = null;
  private shown: { xdesc: string; inst: MeshInstance; duration: number } | null = null;
  private loading: string | null = null;
  private running = false;
  private frame = 0;
  private last = -Infinity;
  private readonly t0 = performance.now();
  private readonly still = matchMedia('(prefers-reduced-motion: reduce)');
  /** The size the last frame was drawn at (a still frame is drawn again only when it changes). */
  private drawn = '';

  constructor() {
    this.el.className = 'backdrop';
    this.el.setAttribute('aria-hidden', 'true');
    this.el.appendChild(this.canvas);
    this.camera.position.set(0, 0, CAMERA_Z);
  }

  /** The theme whose backdrop to show (Themes.txt group name). */
  setTheme(theme: string | null): void {
    if (theme === this.theme) return;
    this.theme = theme;
    if (this.running) void this.load();
  }

  /** Draws from now on (loading the theme's mesh). */
  start(): void {
    if (this.running) return;
    this.running = true;
    void this.load();
    this.frame = requestAnimationFrame(t => this.tick(t));
  }

  /** Stops drawing and releases the GL context (a match needs the GPU); `start` makes a new one. */
  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.gl?.dispose();
    this.gl?.forceContextLoss();
    this.gl = null;
    this.drawn = '';
    this.el.classList.remove('ready');
  }

  /** Shows the theme's mesh once it has loaded (the last one stays meanwhile); never rejects. */
  private async load(): Promise<void> {
    const theme = this.theme;
    try {
      const file = theme ? await themeBackdrop(theme) : null;
      const xdesc = file ? `DataLinux/Objects/UI/${file}` : null;
      if (!xdesc || !this.running || theme !== this.theme || this.shown?.xdesc === xdesc || this.loading === xdesc) return;
      this.loading = xdesc;
      const mesh = findMesh(xdesc.slice(xdesc.lastIndexOf('/') + 1).replace(/\.xdesc$/i, ''), xdesc, null);
      const t = mesh ? await loadTemplate(mesh.glb, mesh.wanim, true) : null;
      if (this.loading === xdesc) this.loading = null;
      if (!t || theme !== this.theme) return;
      const inst = instantiate(t);
      this.scene.clear();
      this.scene.add(inst.root);
      this.shown = { xdesc, inst, duration: t.anim?.clips.find(c => c.name === CLIP)?.duration ?? 0 };
      this.drawn = '';
    } catch (e) {
      this.loading = null;
      console.warn('front-end backdrop', theme, e);
    }
  }

  private tick(now: number): void {
    if (!this.running) return;
    this.frame = requestAnimationFrame(t => this.tick(t));
    if (now - this.last < FRAME_MS - 2) return;
    this.last = now;
    const s = this.shown;
    const w = this.el.clientWidth, h = this.el.clientHeight;
    const size = `${w}x${h}`;
    if (!s || w === 0 || h === 0 || (this.still.matches && this.drawn === size)) return;
    // The buffer at the window's size, at most the design size (the art's own resolution).
    const k = Math.min(1, DESIGN_W / w, DESIGN_H / h);
    const bw = Math.max(1, Math.round(w * k)), bh = Math.max(1, Math.round(h * k));
    if (!this.gl) {
      // A lost context stays with its canvas: every renderer gets a new one.
      const canvas = document.createElement('canvas');
      this.canvas.replaceWith(canvas);
      this.canvas = canvas;
      try {
        this.gl = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
      } catch (e) {
        // No WebGL: the page keeps its CSS background.
        console.warn('front-end backdrop', e);
        this.stop();
        return;
      }
      this.gl.outputColorSpace = THREE.LinearSRGBColorSpace;
      this.gl.setPixelRatio(1);
      this.gl.setClearColor(0x000000, 1);
    }
    if (this.canvas.width !== bw || this.canvas.height !== bh) this.gl.setSize(bw, bh, false);
    const time = this.still.matches ? 0 : (now - this.t0) / 1000;
    poseLayers(s.inst, [{ clip: CLIP, time: s.duration > 0 ? time % s.duration : 0, weight: 1 }]);
    s.inst.root.updateMatrixWorld(true);
    // Cover: the design's full width on a wider window, its full height on a narrower one.
    const aspect = w / h;
    this.camera.aspect = aspect;
    this.camera.fov = (2 * Math.atan(FRUSTUM_X / Math.max(aspect, DESIGN_W / DESIGN_H)) * 180) / Math.PI;
    this.camera.updateProjectionMatrix();
    this.gl.render(this.scene, this.camera);
    this.drawn = size;
    this.el.classList.add('ready');
  }
}
